// Shared grounding exercise metadata for Recovery Trauma™.
// Single source of truth: imported by the /grounding route and by the
// GroundMeNow sheet, which shows non-members the exercise names (locked).

import {
  Eye,
  Flower2,
  Hand,
  Home,
  Leaf,
  Scan,
  Compass,
  Anchor,
  Gem,
  Snowflake,
  Footprints,
} from "lucide-react";
import audioProgressive from "@/assets/grounding-audio/progressive-relaxation.mp3.asset.json";
import cover54321 from "@/assets/grounding-covers/54321-senses-cover.png.asset.json";
import coverSelfSoothe from "@/assets/grounding-covers/self-soothe-kit-cover.png.asset.json";
import coverSafePlace from "@/assets/grounding-covers/safe-place-visualisation-cover.png.asset.json";
import coverProgressive from "@/assets/grounding-covers/progressive-relaxation-cover.png.asset.json";
import coverFeet from "@/assets/grounding-covers/feet-on-the-ground-cover.png.asset.json";
import coverLegsUpWall from "@/assets/grounding-covers/legs-up-the-wall-cover.png.asset.json";
import coverBodyScan from "@/assets/grounding-covers/full-body-scan-cover.png.asset.json";
import video54321 from "@/assets/grounding-videos/54321-senses.mp4.asset.json";
import videoSafePlace from "@/assets/grounding-videos/safe-place.mp4.asset.json";
import videoLegsUpWall from "@/assets/grounding-videos/legs-up-the-wall.mp4.asset.json";
import videoFeet from "@/assets/grounding-videos/feet-on-the-ground.mp4.asset.json";
import videoSensory from "@/assets/grounding-videos/sensory-grounding.mp4.asset.json";

export type Exercise = {
  id: string;
  emoji: string;
  title: string;
  icon: typeof Eye;
  blurb: string;
  duration: string;
  minutes: number;
  bestFor: string[];
  accent: "sage" | "lavender" | "blush" | "warm" | "cream" | "gold";
  steps: string[];
  cover: string;
  audio?: string;
  /** Optional Recovery Trauma™ video. When set, the exercise plays a video instead of step-by-step text/voice. */
  video?: string;
  /** Plain-language explanation shown on the intro screen. */
  whatIsThis: string;
  /** Bullet list shown under "How does it work?" on the intro screen. */
  howItWorks: string[];
  /** Optional longer explanation revealed by tapping "Learn More". */
  learnMore?: string;
};


export const EXERCISES: Exercise[] = [
  {
    id: "5-4-3-2-1",
    emoji: "🌿",
    title: "5-4-3-2-1 Senses",
    icon: Eye,
    blurb: "Anchor in the present moment through your five senses.",
    duration: "3 min",
    minutes: 3,
    bestFor: ["Overwhelm", "Flashbacks", "Anxiety", "Dissociation"],
    accent: "sage",
    cover: cover54321.url,
    video: video54321.url,
    whatIsThis:
      "A sensory grounding exercise that brings attention back to the present moment through the five senses.",
    howItWorks: [
      "5 things you can see",
      "4 things you can feel",
      "3 things you can hear",
      "2 things you can smell",
      "1 thing you can taste",
    ],
    learnMore:
      "5-4-3-2-1 is one of the most widely used grounding techniques in trauma-informed care. By gently moving attention through the senses, it helps signal safety to the nervous system and interrupts overwhelming thoughts or flashbacks. There is no right or wrong way to do it — even noticing two or three things is enough.",
    steps: [
      "Look around and name 5 things you can SEE.",
      "Notice 4 things you can TOUCH or FEEL.",
      "Listen for 3 things you can HEAR.",
      "Notice 2 things you can SMELL.",
      "Notice 1 thing you can TASTE.",
    ],
  },
  {
    id: "self-soothe",
    emoji: "🤍",
    title: "Self-Soothe Kit",
    icon: Flower2,
    blurb: "Gather small comforts for each of the five senses.",
    duration: "5 min",
    minutes: 5,
    bestFor: ["Emotional overwhelm", "Stress", "Comfort"],
    accent: "blush",
    cover: coverSelfSoothe.url,
    whatIsThis:
      "A gentle practice for gathering small, sensory comforts you can reach for when things feel hard.",
    howItWorks: [
      "Choose one thing for sight",
      "One thing for sound",
      "One thing for touch",
      "One thing for smell",
      "One thing for taste",
    ],
    learnMore:
      "A self-soothe kit isn't about fixing how you feel — it's about offering yourself small, sensory kindness in moments of distress. You can keep these items in a drawer, a bag, or simply a mental list. Reach for them when your nervous system needs a softer signal.",
    steps: [
      "SIGHT — a photo, view, or piece of art that softens you.",
      "SOUND — a song, nature audio, or a loved one's voice note.",
      "TOUCH — a blanket, a smooth stone, or warm hands on your chest.",
      "SMELL — tea, an oil, a candle, your favourite jumper.",
      "TASTE — herbal tea, a square of chocolate, a slow sip of water.",
      "Keep these nearby so you can reach for them when you need to.",
    ],
  },
  {
    id: "safe-place",
    emoji: "🏡",
    title: "Safe Place Visualisation",
    icon: Home,
    blurb: "Build a quiet inner sanctuary you can return to.",
    duration: "5 min",
    minutes: 5,
    bestFor: ["Overwhelm", "Anxiety", "Flashbacks"],
    accent: "lavender",
    cover: coverSafePlace.url,
    video: videoSafePlace.url,
    whatIsThis:
      "A guided visualisation that invites you to imagine a place that feels calm, comfortable, or supportive.",
    howItWorks: [
      "Sights",
      "Sounds",
      "Textures",
      "Comfort",
      "Familiar details",
    ],
    learnMore:
      "The safe place doesn't have to be real, and it doesn't have to be perfect. Some people picture a beach, a childhood bedroom, a forest clearing, or somewhere entirely imagined. The aim is to give your nervous system a felt sense of safety it can return to whenever it needs.",
    steps: [
      "Close your eyes and take three slow breaths.",
      "Picture a place — real or imagined — where you feel safe.",
      "Notice the light, the colours, the temperature.",
      "Notice the sounds. Near and far.",
      "Notice what you can feel under your hands, your feet.",
      "Stay here for a few breaths. You can return any time.",
    ],
  },
  {
    id: "legs-up-the-wall",
    emoji: "🧘",
    title: "Legs Up the Wall",
    icon: Leaf,
    blurb: "A restorative pose to soothe the nervous system.",
    duration: "10 min",
    minutes: 10,
    bestFor: ["Stress", "Nervous system regulation", "Fatigue", "Recovery"],
    accent: "sage",
    cover: coverLegsUpWall.url,
    video: videoLegsUpWall.url,
    whatIsThis:
      "A restorative yoga posture that calms the nervous system by gently inverting the legs against a wall.",
    howItWorks: [
      "Sit beside a wall",
      "Lie back and swing your legs up",
      "Soften the body",
      "Breathe slowly with long exhales",
      "Rest as long as feels good",
    ],
    learnMore:
      "Legs Up the Wall is a gentle inversion that helps shift the body out of fight-or-flight by encouraging the parasympathetic (rest-and-digest) response. It can ease tired legs, soften anxiety, and support sleep. Use a folded blanket under the hips for extra comfort if you'd like.",
    steps: [
      "Find a wall. Sit close, then gently swing your legs up.",
      "Let your back, shoulders and head feel supported.",
      "Soften the jaw, the shoulders, the belly.",
      "Breathe slowly. Long exhales.",
      "Notice the legs — warmth, heaviness, release.",
      "When ready, bend the knees, roll to one side, and return to sitting.",
    ],
  },
  {
    id: "body-scan",
    emoji: "👐",
    title: "Full Body Scan",
    icon: Scan,
    blurb: "Move attention slowly through the body to release tension.",
    duration: "10 min",
    minutes: 10,
    bestFor: ["Dissociation", "Tension", "Reconnection"],
    accent: "cream",
    cover: coverBodyScan.url,
    whatIsThis:
      "A guided awareness exercise that gently brings attention to different areas of the body.",
    howItWorks: [
      "Physical sensations",
      "Areas of tension",
      "Areas of comfort",
      "Areas that feel neutral",
    ],
    learnMore:
      "There is nothing you need to change during a body scan — the practice is simply to notice what is present. For some people, parts of the body can feel numb, far away, or hard to sense. That's information too, and it's okay. Move at a pace that feels safe and stop any time.",
    steps: [
      "Find a comfortable position. Let your body be supported.",
      "Bring gentle attention to the top of your head.",
      "Slowly move down — face, jaw, neck, shoulders.",
      "Continue through the arms, hands, chest, belly.",
      "Move into the hips, legs, feet.",
      "Notice without judgment — warmth, tension, ease.",
      "Allow each area to soften as you exhale.",
    ],
  },
  {
    id: "progressive",
    emoji: "🌱",
    title: "Progressive Relaxation",
    icon: Hand,
    blurb: "Gently tense and release muscle groups to discharge tension.",
    duration: "8 min",
    minutes: 8,
    bestFor: ["Dissociation", "Stress", "Sleep"],
    accent: "warm",
    cover: coverProgressive.url,
    audio: audioProgressive.url,
    whatIsThis:
      "A guided exercise that gently tenses and releases muscle groups one by one to help the body discharge held tension.",
    howItWorks: [
      "Tense a muscle group briefly",
      "Hold for a few breaths",
      "Release fully",
      "Notice the difference",
      "Move on to the next area",
    ],
    learnMore:
      "Progressive muscle relaxation can be especially helpful for sleep, stress and people who feel disconnected from the body. Tense gently — never to the point of strain. If tensing any area feels unsafe, you can simply imagine the release instead.",
    steps: [
      "Curl your toes tight for 5 seconds. Release.",
      "Point your toes to tense the calves. Hold. Release.",
      "Tighten the thighs. Hold. Let go.",
      "Clench the fists. Hold. Open the hands wide.",
      "Shrug the shoulders up to the ears. Hold. Drop them.",
      "Gently scrunch the face. Hold. Let everything melt.",
      "Lie still. Notice the quiet that remains.",
    ],
  },
  {
    id: "feet-on-ground",
    emoji: "👣",
    title: "Feet On The Ground",
    icon: Footprints,
    blurb: "A quick somatic anchor through the soles of your feet.",
    duration: "2 min",
    minutes: 2,
    bestFor: ["Dissociation", "Flashbacks", "Quick reset"],
    accent: "gold",
    cover: coverFeet.url,
    video: videoFeet.url,
    whatIsThis:
      "A quick somatic grounding exercise that anchors attention through the soles of your feet.",
    howItWorks: [
      "Press both feet into the floor",
      "Notice the contact",
      "Wiggle the toes",
      "Press into heels and balls of the feet",
      "Take three slow breaths",
    ],
    learnMore:
      "Sometimes called a 'somatic anchor', this is one of the fastest ways to bring the body back to the present. It can be done sitting, standing, or even with shoes on under a desk — no one will know you're doing it.",
    steps: [
      "Place both feet flat on the floor.",
      "Press the soles down — feel the contact.",
      "Wiggle the toes. Notice the shoes, socks, or floor.",
      "Press into the heels. Then the balls of the feet.",
      "Take three slow breaths. You are here. You are now.",
    ],
  },
  {
    id: "orienting",
    emoji: "👁️",
    title: "Orienting To Your Surroundings",
    icon: Compass,
    blurb: "Let your eyes travel slowly around the room, without changing anything.",
    duration: "3 min",
    minutes: 3,
    bestFor: ["Disconnection", "Overwhelm", "External focus"],
    accent: "sage",
    cover: cover54321.url,
    whatIsThis:
      "A slow, external looking practice. You simply let your eyes move around the space you are in and notice what is there.",
    howItWorks: [
      "Where you are right now",
      "What is behind and beside you",
      "Colours, shapes and light",
      "Anything that feels neutral or pleasant",
    ],
    learnMore:
      "Some people find that looking outward feels safer than looking inward. Orienting keeps your attention on the room rather than on sensations inside the body, which some people prefer when they feel disconnected. Move slowly, keep your eyes open, and stop whenever you would like to.",
    steps: [
      "Let your eyes move slowly to the left. No rush.",
      "Let them travel to the right, taking in the whole space.",
      "Turn your head gently and notice what is behind you.",
      "Name three things you can see, quietly or out loud.",
      "Notice one thing that feels neutral or pleasant to look at.",
      "Rest your eyes there for a few breaths.",
    ],
  },
  {
    id: "sensory-grounding",
    emoji: "✋",
    title: "Sensory Grounding",
    icon: Hand,
    blurb: "Use touch, sound and sight to notice what is here right now.",
    duration: "4 min",
    minutes: 4,
    bestFor: ["Overwhelm", "Anxiety", "Disconnection"],
    accent: "blush",
    cover: coverSelfSoothe.url,
    video: videoSensory.url,
    whatIsThis:
      "A short practice that moves through a few senses so your attention has something concrete to rest on.",
    howItWorks: [
      "Something you can touch",
      "Something you can hear",
      "Something you can see",
      "Something you can smell, if available",
    ],
    learnMore:
      "You do not need to use every sense. Some people find one is enough. If a sense feels uncomfortable today, skip it and move to the next one.",
    steps: [
      "Find something near you that you can touch. Notice its texture and temperature.",
      "Listen for a sound. Near or far, it does not matter which.",
      "Look for one colour in the room and follow it with your eyes.",
      "If there is a scent nearby, notice it. If not, skip this step.",
      "Take one slower breath out, at your own pace.",
    ],
  },
  {
    id: "body-anchors",
    emoji: "🫱",
    title: "Body Anchors",
    icon: Anchor,
    blurb: "Notice the places where your body is supported right now.",
    duration: "3 min",
    minutes: 3,
    bestFor: ["Disconnection", "Panic", "Quick reset"],
    accent: "gold",
    cover: coverFeet.url,
    whatIsThis:
      "A short practice that brings attention to the points of contact between your body and whatever is holding you.",
    howItWorks: [
      "Feet on the floor",
      "Back against the chair",
      "Hands resting somewhere",
      "The weight of your body being held",
    ],
    learnMore:
      "Contact points can feel more manageable than the whole body. If noticing your body feels too much today, you might prefer Orienting instead, which keeps attention outside of you.",
    steps: [
      "Notice where your feet are. Floor, bed, sofa — whatever is there.",
      "Notice where your back or hips are supported.",
      "Rest your hands somewhere and notice the contact.",
      "Press gently into one contact point, then release.",
      "Let the surface hold your weight for a few breaths.",
    ],
  },
  {
    id: "object-grounding",
    emoji: "🪨",
    title: "Object Grounding",
    icon: Gem,
    blurb: "Hold one object and explore it slowly with your attention.",
    duration: "3 min",
    minutes: 3,
    bestFor: ["Overwhelm", "Racing thoughts", "Quick reset"],
    accent: "cream",
    cover: coverSelfSoothe.url,
    whatIsThis:
      "A simple practice using one object you can hold — a stone, a mug, a key, a piece of fabric.",
    howItWorks: [
      "Weight and temperature",
      "Texture and edges",
      "Colour and detail",
      "How it feels to hold",
    ],
    learnMore:
      "Some people carry a small object with them for exactly this reason. There is nothing special about the object itself — it simply gives your attention somewhere concrete to go.",
    steps: [
      "Pick up something within reach.",
      "Notice its weight in your hand.",
      "Notice whether it feels warm, cool or neutral.",
      "Move your fingers over it. Notice edges, texture, detail.",
      "Look at it as though you are seeing it for the first time.",
    ],
  },
  {
    id: "temperature",
    emoji: "🧊",
    title: "Temperature & Sensation",
    icon: Snowflake,
    blurb: "A change in temperature can give your attention something clear to notice.",
    duration: "2 min",
    minutes: 2,
    bestFor: ["Panic", "Flashbacks", "Shut down"],
    accent: "lavender",
    cover: coverProgressive.url,
    whatIsThis:
      "A quick practice using cool or warm sensation — cool water on the hands, a cold glass, a warm drink or a blanket.",
    howItWorks: [
      "A cool or warm surface",
      "Where you feel the change",
      "How long the sensation lasts",
      "What feels tolerable for you",
    ],
    learnMore:
      "Keep it gentle. Cool tap water or holding a cold glass is enough — nothing extreme, and nothing that hurts. Some people prefer warmth instead, which is equally fine.",
    steps: [
      "Find something cool or warm — water, a glass, a mug, a blanket.",
      "Rest your hands on it and notice the first moment of contact.",
      "Notice where in your hands you feel it most.",
      "Stay with it for a few breaths, only as long as feels comfortable.",
      "Let go and notice how your hands feel afterwards.",
    ],
  },
];
